import React from 'react';
import { Drawer, DrawerBody } from '@fluentui/react-components';
import { ChevronCircleLeft20Filled, ChevronCircleRight20Filled, Delete12Regular, Dismiss20Regular, Edit12Regular } from '@fluentui/react-icons';
import styles from './css/index.module.css';
import { CustomAvatar } from './index';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteTaskComment, GetTaskComment, PatchTaskComment, PostTaskComment } from '../../redux/taskSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format } from 'date-fns';
import SafeHTML from '../SafeHTML';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
const TaskDetailsSidebar = ({ open, onClose, task, onNavigateTask, hasPrev = false, hasNext = false }) => {
    const dispatch = useDispatch();
    const { taskComments, taskCommentBtnLoading } = useSelector(state => state.task);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [comment, setComment] = React.useState('');
    const quillRef = React.useRef(null);
    const [editingCommentId, setEditingCommentId] = React.useState(null);
    const [editingCommentText, setEditingCommentText] = React.useState('');
    const editQuillRef = React.useRef(null);

    React.useEffect(() => {
        if (task) dispatch(GetTaskComment({ taskId: task?.id }));
    }, [task, dispatch]);

    const handleSubmit = async () => {
        let body = {
            task: task?.id,
            comment,
            created_by: user?.id,
        };
        dispatch(PostTaskComment({ body }));
        setComment('');
        // Clear the Quill editor
        if (quillRef.current) {
            quillRef.current.getEditor().setText('');
        }
    };

    const handleEditComment = commentItem => {
        setEditingCommentId(commentItem.id);
        setEditingCommentText(commentItem.comment);
    };

    const handleSaveEdit = async () => {
        if (editingCommentText.trim() && editingCommentId) {
            dispatch(
                PatchTaskComment({
                    commentId: editingCommentId,
                    body: { comment: editingCommentText },
                }),
            );
            setEditingCommentId(null);
            setEditingCommentText('');
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const handleGetProjectName = (id, onlyName) => {
        let userData = projectUsersAll.find(item => item.value.user_id === id);
        if (!userData && user?.id === id) {
            userData = {
                value: { ...user },
            };
        }

        return onlyName ? (
            <span> {userData?.value?.full_name}</span>
        ) : (
            <div className="d-flex align-items-center">
                <div className={styles.smallAvatar} style={{ background: userData?.color }}>
                    {getProfileNameComponent(userData?.value?.full_name)}
                </div>
                <span style={{ maxWidth: 120 }} className={styles.smallFont + ' m-0 ms-2 text-truncate'}>
                    {userData?.value?.full_name}
                </span>
            </div>
        );
    };

    const getProfileNameComponent = fullName => {
        if (!fullName) return '';
        const names = fullName.split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[1][0]).toUpperCase();
        }
        return fullName[0]?.toUpperCase() || '';
    };

    return (
        <Drawer
            type="overlay"
            position="end"
            open={open}
            onOpenChange={(e, data) => {
                if (!data.open) {
                    onClose();
                }
            }}
            size="medium">
            <DrawerBody>
                <div className="mt-4 d-flex justify-content-between">
                    <div>
                        <div className="mb-4">
                            <span
                                role="button"
                                onClick={() => onNavigateTask && hasPrev && onNavigateTask('prev')}
                                style={{ cursor: hasPrev ? 'pointer' : 'not-allowed', opacity: hasPrev ? 1 : 0.4 }}>
                                <ChevronCircleLeft20Filled color="var(--Brand-Secondary)" />
                            </span>
                            <span
                                className="ms-5"
                                role="button"
                                onClick={() => onNavigateTask && hasNext && onNavigateTask('next')}
                                style={{ cursor: hasNext ? 'pointer' : 'not-allowed', opacity: hasNext ? 1 : 0.4 }}>
                                <ChevronCircleRight20Filled color="var(--Brand-Secondary)" />
                            </span>
                        </div>
                        <p className={styles.sidebarWsTagLabel}>
                            {task?.workstream_repr}{' '}
                            {(() => {
                                const tagsArray = Array.isArray(task?.tags) && task.tags.length > 0 ? task.tags : task?.tag_repr ? [task.tag_repr] : [];
                                if (tagsArray.length === 0) return '';
                                return `/ ${tagsArray.join(', ')}`;
                            })()}
                        </p>
                        <p className={styles.sidebarTaskText}>{task?.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : ''}</p>
                    </div>
                    <div className="ms-2">
                        <Dismiss20Regular role="button" color="var(--Neutral-Gray-6, #989BAD)" onClick={onClose} />
                    </div>
                </div>
                <div className="mt-3">
                    {/* Added On info */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <div>
                            <CustomAvatar name={task?.created_by_name} size={20} />
                        </div>
                        <span className={styles.sidebarWsTagLabel + ' mb-0'}>
                            {task?.created ? `Added on ${format(new Date(task.created), 'dd/MM/yyyy')}` : 'Added on -- --'}
                        </span>
                    </div>

                    {/* Deadline info with history */}
                    <div className="mb-2">
                        {task?.deadline ? (
                            <div>
                                <span className={styles.sidebarWsTagLabel + ' mb-0'}>
                                    <span className={styles.sidebarWsTagLabel + ' mb-2'}>Deadline:</span>{' '}
                                    {format(new Date(task.deadline), 'dd/MM/yyyy')}
                                </span>
                                {/* Show deadline history if available */}
                                {task?.deadline_history && Array.isArray(task.deadline_history) && task.deadline_history.length > 0 && (
                                    <div className="mt-2" style={{ fontSize: '11px', color: '#829ab1' }}>
                                        {task.deadline_history.map((entry, index) => (
                                            <div key={index} className="mb-0">
                                                <span style={{ textDecoration: 'line-through', marginRight: 8 }}>
                                                    {entry.deadline ? format(new Date(entry.deadline), 'dd/MM/yyyy') : '-- --'}
                                                </span>
                                                <span style={{ fontSize: '10px' }}>
                                                    by {entry.changed_by || 'System'} on {entry.updated_on ? format(new Date(entry.updated_on), 'dd/MM/yyyy') : '-- --'}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className={styles.sidebarWsTagLabel + ' mb-0'}>-- --</span>
                        )}
                    </div>
                </div>
                <div className={styles.divider} />

                <p className={styles.sidebarWsTagLabel}>Comments</p>
                {/* Comments Section */}
                {taskComments?.length > 0 && (
                    <div className="mt-4">
                        {taskComments?.map(item => (
                            <div key={item?.id} className="mb-4">
                                {editingCommentId === item?.id ? (
                                    // Edit mode
                                    <div>
                                        <ReactQuill
                                            ref={editQuillRef}
                                            placeholder={'Edit comment'}
                                            modules={{
                                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                            }}
                                            theme="snow"
                                            className="mt-1"
                                            defaultValue={editingCommentText}
                                            onChange={t => {
                                                if (t === '<p><br></p>') setEditingCommentText('');
                                                else setEditingCommentText(t);
                                            }}
                                        />
                                        <div className="mt-2 d-flex gap-2">
                                            <button onClick={handleSaveEdit} disabled={!editingCommentText.trim()} type="button" className="btn btn-primary btn-sm">
                                                Save
                                            </button>
                                            <button onClick={handleCancelEdit} type="button" className="btn btn-secondary btn-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // View mode
                                    <>
                                        <SafeHTML html={item?.comment} className={styles.commentText} tag="p" />
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className={styles.sidebarWsTagLabel + ' mb-0 mt-1'}>
                                                {handleGetProjectName(item?.created_by, true)} {item?.created && format(new Date(item?.created), 'dd/MM/yyyy')}
                                            </div>
                                            {item?.created_by === user?.id && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <DoubleConfirmationModal
                                                        message="Deleting a comment will permanently remove it from the Task."
                                                        handleAction={() => {
                                                            if (taskCommentBtnLoading) return;
                                                            if (!item?.id || !task?.id) return;

                                                            // If user deletes the comment currently in edit mode, exit edit mode.
                                                            if (editingCommentId === item.id) {
                                                                setEditingCommentId(null);
                                                                setEditingCommentText('');
                                                            }

                                                            dispatch(
                                                                DeleteTaskComment({
                                                                    commentId: item.id,
                                                                    taskId: task.id,
                                                                }),
                                                            );
                                                        }}>
                                                        <Delete12Regular
                                                            cursor="pointer"
                                                            color="var(--Brand-Secondary)"
                                                            style={{ opacity: taskCommentBtnLoading ? 0.5 : 1, pointerEvents: taskCommentBtnLoading ? 'none' : 'auto' }}
                                                        />
                                                    </DoubleConfirmationModal>
                                                    <div>
                                                        {' '}
                                                        <Edit12Regular
                                                            cursor="pointer"
                                                            onClick={() => handleEditComment(item)}
                                                            color="var(--Brand-Secondary)"
                                                            style={{ opacity: taskCommentBtnLoading ? 0.5 : 1, pointerEvents: taskCommentBtnLoading ? 'none' : 'auto' }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Add Comment Section */}
                <div className="mt-2">
                    <ReactQuill
                        ref={quillRef}
                        placeholder={'Add a comment'}
                        modules={{
                            toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                        }}
                        theme="snow"
                        className="mt-1"
                        defaultValue={comment}
                        onChange={t => {
                            if (t === '<p><br></p>') setComment('');
                            else setComment(t);
                        }}
                    />
                </div>
                {comment && (
                    <div className="mt-4 d-flex justify-content-between align-items-center">
                        <button onClick={handleSubmit} disabled={!comment} type="button" className="btn btn-primary btn-sm">
                            Add Comment {taskCommentBtnLoading && <span className="spinner-border spinner-border-sm ms-1" />}
                        </button>
                    </div>
                )}
            </DrawerBody>
        </Drawer>
    );
};

export default TaskDetailsSidebar;
